import "./DestinationCardMenu.scss";
import { Dropdown, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis, faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import React from "react";
import { AnyDestination, DestinationType } from "userful-chronos-app-common-js/dist/models/mapping/MappingGroups";
import { useMappingDispatch, useMappingSelector } from "userful-chronos-common-store/dist/mapping";
import { uclientsSliceActions } from "userful-chronos-common-store/dist/destinations";
import { CdmPhysicalVideoDest } from "userful-chronos-app-common-js/dist/models/cdm/CdmPhysical";
import { StringID, isIDNotSet } from "userful-chronos-app-common-js/dist/models/common";
import { CdmVideoWallDest } from "userful-chronos-app-common-js/dist/models/cdm/CdmVideoWall";
import CustomToggleClient from "../../../Displays/CustomToggleClient";
import CustomMenuClient from "../../../Displays/CustomMenuClient";
import DropdownItemUI from "../../../Widgets/Dropdown/DropdownItemUI";
import UClientVolumeControls from "../../audioControls/UClientVolumeControls";
import MultiUClientVolumeControls from "../../audioControls/MultiUClientVolumeControls";

interface IProps {
    destination: AnyDestination;
    type: DestinationType;
    disabled?: boolean;
    className?: string;
}

export default function DestinationCardMenu(props: IProps) {
    const { t } = useTranslation();
    const dispatch = useMappingDispatch();

    const uclients = useMappingSelector((state) => state.uclientsStore.displays);
    const showingIDs = useMappingSelector((state) => state.uclientsStore.showIDDisplays);

    // Find UClient for physical video destination
    const foundUClient =
        props.type === "physicalVideo"
            ? uclients.find(
                  (u) => u.data.id.value === (props.destination as CdmPhysicalVideoDest).endPoint?.id?.seatID?.value,
              )
            : undefined;

    // Get all UClients for a video wall destination
    const videoWallUClients =
        props.type === "videoWalls"
            ? uclients.filter((u) => {
                  const videoWall = props.destination as CdmVideoWallDest;
                  return videoWall.displays.some(
                      (d) => !isIDNotSet(d.endPoint.id.seatID) && u.data.id.value === d.endPoint.id.seatID.value,
                  );
              })
            : [];

    // Define base conditions
    const hasValidPhysicalVideo = props.type === "physicalVideo" && foundUClient !== undefined;
    const hasValidVideoWall = props.type === "videoWalls" && videoWallUClients.length > 0;

    const canToggleShowID =
        (props.type === "physicalVideo" && (props.destination as CdmPhysicalVideoDest).endPoint.type === "UCLIENT") ||
        props.type === "videoWalls";

    const canAdjustVolume = hasValidPhysicalVideo || hasValidVideoWall;

    const currentSeatIDs: StringID[] =
        (props.type === "physicalVideo" && [(props.destination as CdmPhysicalVideoDest).endPoint.id.seatID]) ||
        (props.type === "videoWalls" &&
            (props.destination as CdmVideoWallDest).displays
                .map((d) => d.endPoint.id.seatID)
                .filter((s) => !isIDNotSet(s))) ||
        [];

    const isShowingID = showingIDs.findIndex((s) => currentSeatIDs.findIndex((c) => c.value === s.value) >= 0) >= 0;
    const handleToggleShowID = () => {
        if (isShowingID) {
            dispatch(uclientsSliceActions.hideStationID(currentSeatIDs));
        } else {
            dispatch(uclientsSliceActions.showStationID(currentSeatIDs));
        }
    };

    const hasActions = canToggleShowID || canAdjustVolume;
    if (!hasActions) {
        return null;
    }

    return (
        <>
            <div
                className={`mappingCardActionsSingleMenu destinationVolumn ${props.className ? props.className : ""}`}
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                // onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
            >
                <Dropdown className={`addBorderDropdownMapper ${props.disabled && "disableDropdownNotAllowed"}`}>
                    <Dropdown.Toggle
                        as={CustomToggleClient}
                        id="dropdown-custom-components"
                        className={`custom-dropdown-toggle ${props.disabled && "disableDropdown"}`}
                    >
                        <FontAwesomeIcon icon={faEllipsis} className="ellipsisDropdown" />
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                        as={CustomMenuClient}
                        className="custom-dropdown-menu"
                        popperConfig={{ strategy: "fixed" }}
                        renderOnMount={true}
                    >
                        {canToggleShowID && (
                            <Dropdown.Item as="li" eventKey="1" onClick={handleToggleShowID}>
                                {isShowingID ? (
                                    <DropdownItemUI text={t("CommonUI.hideID")} icon={faEyeSlash} />
                                ) : (
                                    <DropdownItemUI text={t("CommonUI.showID")} icon={faEye} />
                                )}
                            </Dropdown.Item>
                        )}

                        {hasValidPhysicalVideo && <UClientVolumeControls uclient={foundUClient.data} />}

                        {hasValidVideoWall && (
                            <MultiUClientVolumeControls
                                uclients={videoWallUClients.map((u) => u.data)}
                                label={t("CommonUI.videoWallVolume")}
                            />
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </>
    );
}
